import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Popover } from '../../../ui';
import { fetchConversionActions, type ConversionAction } from '../../../../lib/googleAds';

interface LeadActionsPickerProps {
  customerId: string;
  selected: string[];
  /** Called with the new set and the human-readable before/after, when the person saves a change. */
  onSave: (ids: string[], before: string, after: string) => Promise<void>;
}

const describe = (ids: string[], actions: ConversionAction[]) =>
  ids.length === 0 ? 'primary conversions' : ids.map((id) => actions.find((a) => a.id === id)?.name ?? id).join(' + ');

/**
 * What counts as a lead for CPA. Nothing picked = the account's primary conversions, which is rarely right:
 * primary flags flip, and most accounts carry a dead or double-counting action marked primary. Saving a change
 * rebuilds the history on the new definition, since a CPA line that mixes two definitions means nothing.
 */
export default function LeadActionsPicker({ customerId, selected, onSave }: LeadActionsPickerProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState<ConversionAction[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>(selected);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setActions(null);
    setLoadError(null);
    fetchConversionActions(customerId)
      .then((list) => live && setActions(list))
      .catch((err) => live && setLoadError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [customerId]);

  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  const changed = draft.length !== selected.length || draft.some((id) => !selected.includes(id));

  const save = async () => {
    if (!actions) return;
    setSaving(true);
    try {
      await onSave(draft, describe(selected, actions), describe(draft, actions));
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const summary = actions ? describe(selected, actions) : selected.length ? `${selected.length} action${selected.length === 1 ? '' : 's'}` : 'primary conversions';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="hover-wash pressable"
        onClick={() => setOpen((o) => !o)}
        title="Which conversion actions count as a lead"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          maxWidth: '100%',
          padding: '4px 8px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-raised)',
          color: 'var(--text-body)',
          fontSize: 'var(--text-xs)',
          cursor: 'pointer',
        }}
      >
        <span className="t-eyebrow" style={{ margin: 0 }}>Leads</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        {selected.length === 0 ? <Badge tone="warn">default</Badge> : null}
      </button>
      <Popover open={open} anchorEl={buttonRef.current} onClose={() => setOpen(false)} offset={6} minWidth={360} style={{ padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Count these as leads (all conversions per action). Pick none to use whatever the account marks primary.
          </p>
          {loadError ? (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }}>{loadError}</span>
          ) : !actions ? (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Loading conversion actions…</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: 320, overflowY: 'auto' }}>
              {actions.map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <Checkbox
                    checked={draft.includes(a.id)}
                    onChange={(on) => setDraft((d) => (on ? [...d, a.id] : d.filter((id) => id !== a.id)))}
                    label={<span style={{ fontSize: 'var(--text-sm)', color: a.status === 'ENABLED' ? 'var(--text-body)' : 'var(--text-faint)' }}>{a.name}</span>}
                  />
                  <span style={{ display: 'inline-flex', gap: '4px', flexShrink: 0 }}>
                    {a.primary ? <Badge tone="info">primary</Badge> : null}
                    {a.status !== 'ENABLED' ? <Badge tone="idle">{a.status.toLowerCase()}</Badge> : null}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>Saving rebuilds the CPA history.</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" variant="primary" disabled={!changed || saving || !actions} onClick={save}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
}
